import { Component, computed, inject, input, model, output } from '@angular/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DateHelper, DateTimeHelper, TimeService } from '@vectronic/util';
import { Moment } from 'moment';

@Component({
  selector: 'vectronic-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule],
})
export class DatePickerComponent {
  readonly dateFormat = DateTimeHelper.DATE_FORMAT;
  timeService = inject(TimeService);

  useLMT = input<boolean>(false);
  type = input<'startOfDay' | 'endOfDay'>('startOfDay');
  label = input<string>();
  showFormatHint = input<boolean>(true);
  min = input<string | undefined>();
  max = input<string | undefined>();
  openOnFocus = input<boolean>(false);
  value = model<string | undefined>();
  dateChange = output<string | undefined>();

  timeZone = computed(() => (this.useLMT() ? this.timeService.localTimeZone() : undefined));

  pickerValue = computed(() => {
    const value = this.value();
    if (!value) return;
    const timeZone = this.timeZone();
    return timeZone
      ? DateHelper.isoToTimezoneDatePickerDate(value, timeZone)
      : DateHelper.isoToDatePickerDate(value);
  });

  minDate = computed(() => {
    const min = this.min();
    if (!min) return;
    const timeZone = this.timeZone();
    return timeZone
      ? DateHelper.isoToTimezoneDatePickerDate(min, timeZone)
      : DateHelper.isoToDatePickerDate(min);
  });

  maxDate = computed(() => {
    const max = this.max();
    if (!max) return;
    const timeZone = this.timeZone();
    return timeZone
      ? DateHelper.isoToTimezoneDatePickerDate(max, timeZone)
      : DateHelper.isoToDatePickerDate(max);
  });

  onDateChange({ value }: MatDatepickerInputEvent<Moment>) {
    const timeZone = this.timeZone();
    const isStart = this.type() === 'startOfDay';
    let isoDate: string | undefined;
    if (value) {
      if (timeZone) {
        isoDate = isStart
          ? DateHelper.timezoneDatePickerMomentToIsoDayStart(value, timeZone)
          : DateHelper.timezoneDatePickerMomentToIsoDayEnd(value, timeZone);
      } else {
        isoDate = isStart
          ? DateHelper.datePickerMomentToIsoDayStart(value)
          : DateHelper.datePickerMomentToIsoDayEnd(value);
      }
    }
    this.value.set(isoDate);
    this.dateChange.emit(isoDate);
  }
}
